import React, { useEffect, useState } from 'react';
import { Building2, Users, Monitor, AlertTriangle, RefreshCw, Plus, ExternalLink, Copy, CheckCircle, XCircle, ChevronRight } from 'lucide-react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://itil.tier5.com.ar/api';

const PLAN_MAP = {
  pro:        { label: 'Pro',        color: '#0063A5', bg: '#E6F1FB' },
  enterprise: { label: 'Enterprise', color: '#6B3FA0', bg: '#F5F0FF' },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newTenant, setNewTenant] = useState(null);
  const [form, setForm] = useState({ name: '', subdomain: '', plan: 'pro', allowed_domains: '' });
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantStats, setTenantStats] = useState({});

  const token = localStorage.getItem('admin_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    fetch(`${API_URL}/tenants`, { headers })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setTenants(list);
        // Cargar stats de cada tenant
        list.forEach(t => {
          fetch(`${API_URL}/tenants/${t.id}/stats`, { headers })
            .then(r => r.json())
            .then(s => setTenantStats(prev => ({ ...prev, [t.id]: s.stats })))
            .catch(() => {});
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/tenants`, { method: 'POST', headers, body: JSON.stringify(form) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setNewTenant(data);
      setShowModal(false);
      setForm({ name: '', subdomain: '', plan: 'pro', allowed_domains: '' });
      load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleToggle = async (t) => {
    if (t.subdomain === 'itil') return;
    await fetch(`${API_URL}/tenants/${t.id}`, { method: 'PATCH', headers, body: JSON.stringify({ is_active: !t.is_active }) });
    load();
  };

  const copy = (text) => navigator.clipboard.writeText(text);

  const totalAssets = Object.values(tenantStats).reduce((a, s) => a + parseInt(s?.total_assets || 0), 0);
  const totalUsers = Object.values(tenantStats).reduce((a, s) => a + parseInt(s?.total_users || 0), 0);
  const totalIncidents = Object.values(tenantStats).reduce((a, s) => a + parseInt(s?.open_incidents || 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: '#F0F4F8' }}>
      {/* Header */}
      <div style={{ background: '#0A1628', padding: '0 32px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 32, height: 32, background: '#0063A5', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12 }}>IT</div>
          <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>ITIL Suite</span>
          <span style={{ color: '#4A6278', fontSize: 13 }}>/ Panel de administración</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: '#8BAABF', fontSize: 13 }}>{user?.full_name}</span>
          <button onClick={logout} style={{ background: 'none', border: '1px solid #4A6278', color: '#8BAABF', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
            Cerrar sesión
          </button>
        </div>
      </div>

      <div style={{ padding: '32px', maxWidth: 1200, margin: '0 auto' }}>
        {/* Title */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>Empresas clientes</div>
            <div style={{ fontSize: 13, color: '#666' }}>Gestión de tenants · ITIL Suite</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={load} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <RefreshCw size={14} /> Actualizar
            </button>
            <button onClick={() => { setShowModal(true); setError(''); setNewTenant(null); }} style={{ background: '#0063A5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
              <Plus size={14} /> Nueva empresa
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
          {[
            ['Empresas activas', tenants.filter(t => t.is_active).length, '#0063A5', Building2],
            ['Total activos', totalAssets, '#1A6E2E', Monitor],
            ['Total usuarios', totalUsers, '#6B3FA0', Users],
            ['Incidentes abiertos', totalIncidents, '#D45D00', AlertTriangle],
          ].map(([label, val, color, Icon]) => (
            <div key={label} style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: '#888' }}>{label}</span>
                <Icon size={16} color={color} style={{ opacity: 0.6 }} />
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Resultado nueva empresa */}
        {newTenant && (
          <div style={{ background: '#EAF4ED', border: '1px solid #8DD5A0', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1A6E2E', marginBottom: 10 }}>✓ "{newTenant.tenant?.name}" creada correctamente</div>
            {newTenant.next_steps?.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: '#1A6E2E', fontFamily: 'monospace', background: '#fff', padding: '4px 10px', borderRadius: 6, border: '1px solid #8DD5A0', flex: 1 }}>{step}</span>
                <button onClick={() => copy(step.split(': ')[1] || step)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6E2E' }}><Copy size={14} /></button>
              </div>
            ))}
          </div>
        )}

        {/* Tabla de tenants */}
        <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Empresa', 'Subdominio', 'Plan', 'Activos', 'Usuarios', 'Incidentes', 'Estado', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#888' }}>Cargando...</td></tr>
              ) : tenants.map(t => {
                const plan = PLAN_MAP[t.plan] || PLAN_MAP.pro;
                const stats = tenantStats[t.id];
                return (
                  <tr key={t.id} style={{ borderBottom: '1px solid #F0F4F8', opacity: t.is_active ? 1 : 0.5 }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace' }}>{t.db_schema}</div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#0063A5' }}>
  {t.subdomain === 'itil' ? 'itil.tier5.com.ar' : `${t.subdomain}.itil.tier5.com.ar`}
</span>
                        <a href={t.subdomain === 'itil' ? 'https://itil.tier5.com.ar' : `https://${t.subdomain}.itil.tier5.com.ar`} target="_blank" rel="noreferrer" style={{ color: '#0063A5' }}><ExternalLink size={11} /></a>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ background: plan.bg, color: plan.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500 }}>{plan.label}</span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 14, fontWeight: 600, color: '#0063A5' }}>{stats?.total_assets || '—'}</td>
                    <td style={{ padding: '14px 16px', fontSize: 14, fontWeight: 600, color: '#6B3FA0' }}>{stats?.total_users || '—'}</td>
                    <td style={{ padding: '14px 16px', fontSize: 14, fontWeight: 600, color: '#D45D00' }}>{stats?.open_incidents || '—'}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: t.is_active ? '#1A6E2E' : '#C0392B' }}>
                        {t.is_active ? <CheckCircle size={13} /> : <XCircle size={13} />}
                        {t.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {t.subdomain !== 'itil' && (
                        <button onClick={() => handleToggle(t)} style={{
                          background: 'none', border: '1px solid #ddd', borderRadius: 6,
                          padding: '5px 10px', cursor: 'pointer', fontSize: 12,
                          color: t.is_active ? '#D45D00' : '#1A6E2E'
                        }}>
                          {t.is_active ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal nueva empresa */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 600 }}>Nueva empresa cliente</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#888' }}>✕</button>
            </div>
            {error && <div style={{ background: '#FDECEB', border: '1px solid #F5C0BC', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#C0392B', marginBottom: 16 }}>{error}</div>}
            <form onSubmit={handleSave}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#444', display: 'block', marginBottom: 6 }}>Nombre de la empresa *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Ej: Empresa SA"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#444', display: 'block', marginBottom: 6 }}>Subdominio *</label>
                <div style={{ display: 'flex' }}>
                  <input value={form.subdomain} onChange={e => setForm(f => ({ ...f, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                    required placeholder="empresasa"
                    style={{ flex: 1, padding: '9px 12px', border: '1px solid #ddd', borderRadius: '8px 0 0 8px', fontSize: 13 }} />
                  <span style={{ background: '#f5f5f5', border: '1px solid #ddd', borderLeft: 'none', padding: '9px 12px', borderRadius: '0 8px 8px 0', fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
                    .itil.tier5.com.ar
                  </span>
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#444', display: 'block', marginBottom: 6 }}>Plan *</label>
                <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }}>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#444', display: 'block', marginBottom: 6 }}>Dominios permitidos para login</label>
                <input value={form.allowed_domains} onChange={e => setForm(f => ({ ...f, allowed_domains: e.target.value }))}
                  placeholder="empresa.com,gmail.com"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
                <button type="submit" disabled={saving} style={{ background: '#0063A5', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                  {saving ? 'Creando...' : 'Crear empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
